import React from 'react';
import jsPDF from 'jspdf';
import GetAppIcon from '@material-ui/icons/GetApp';
import { messagesRequest } from 'modules/messages/actions';
import i18n from 'i18n';
import palette from 'theme/palette';
import autoTable, { RowInput } from 'jspdf-autotable';
import {
  ClickAwayListener,
  Grow,
  makeStyles,
  MenuItem,
  IconButton,
  MenuList,
  Paper,
  Popper,
  ButtonGroup,
} from '@material-ui/core';
import { connect } from 'react-redux';
import { Filter, getOperatorByValue, conditionOperators } from 'utils/filters';
import styles from './styles';

const useStyles = makeStyles(styles);

export interface ButtonExportBaseProps {
  context?: string;
  getHeadersPDF?: RowInput[] | undefined;
  getBodyListPDF?: RowInput[] | undefined;
  filters: Record<string, string | string[]>;
  filterOptions: Filter[];
  messagesRequest: (props) => void;
  csvAction?: () => void;
}

const ButtonExport = ({
  context,
  getHeadersPDF,
  getBodyListPDF,
  filters,
  filterOptions,
  messagesRequest,
  csvAction,
}: ButtonExportBaseProps) => {
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);

  const anchorRef = React.useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen(prev => !prev);
  };

  const handleClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const handleOpen = (
    key: string | undefined,
    event: React.MouseEvent<HTMLElement>,
    onClick?: () => void
  ) => {
    if (onClick) {
      onClick();
    }

    if (!key) {
      setOpen(false);
      // eslint-disable-next-line no-useless-return
    } else if (!anchorRef.current) return;
  };

  const getFilterValue = (filter: Filter, value: string | string[]) => {
    if (!filter.isSelect && !filter.multiple) return value;

    if (filter.isSelect) {
      if (Array.isArray(value)) {
        return filter.selectOptions
          ?.filter(item => value.includes(item.value))
          .map(item => `'${i18n.t(item.label)}'`)
          .join(` ${i18n.t('OR_KEY').toLowerCase()} `);
      }

      const label =
        filter.selectOptions?.find(item => item.value === value)?.label || '';
      return i18n.t(label);
    }

    if (Array.isArray(value))
      return (value as string[])
        .map(item => `'${item}'`)
        .join(` ${i18n.t('OR_KEY').toLowerCase()} `);

    return value;
  };

  const getAppliedFilters = (): string[] => {
    if (!filters) return [];

    const appliedFilters = Object.entries(filters).reduce(
      (acc, [key, value]) => {
        let field = key;
        let operator = '';

        if (key.includes('__')) {
          [field, operator] = key.split('__');
          operator = `__${operator}`;
        } else if (key.endsWith('!')) {
          field = key.slice(0, -1);
          operator = '!';
        }

        const filter = filterOptions.find(item => item.field === field);

        if (filter) {
          acc.push({
            filter: i18n.t(filter.label),
            value: getFilterValue(filter, value),
            operator: getOperatorByValue(operator),
          });
        }

        return acc;
      },
      [] as any[]
    );

    return appliedFilters?.map(item => {
      const operatorLabel = i18n.t(conditionOperators[item.operator].label);
      const operatorLabelInParentheses =
        operatorLabel.match(/\(([^)]+)\)/)?.[1] || '';

      return `${item.filter} ${operatorLabelInParentheses}: ${item.value}`;
    });
  };

  const getTopMarginByFilterCount = (filterCount: number) => {
    if (filterCount === 0) return 35;
    if (filterCount === 1) return 50;
    if (filterCount > 1 && filterCount < 5) return 75;
    if (filterCount >= 5 && filterCount < 10) return 105;
    if (filterCount >= 10 && filterCount < 15) return 120;
    if (filterCount >= 15 && filterCount < 22) return 140;
    return 85;
  };

  // eslint-disable-next-line new-cap
  const doc = new jsPDF({ format: 'a4', orientation: 'landscape' });

  const generatePDFButton = () => {
    const appliedFilters = getAppliedFilters();
    const filterCount = appliedFilters?.length;
    const topMargin = getTopMarginByFilterCount(filterCount);

    let pageNumber = 1;
    doc.setFontSize(12);
    const image = document.createElement('img');
    image.setAttribute('src', '/images/skyoneNewLogo.png');
    doc.text(
      [
        '',
        `${i18n.t('LISTING_KEY')}: ${context}`,
        `${i18n.t('PDF_GENERATED_AT_KEY')}: ${new Date().toLocaleString(
          'pt-BR',
          {
            hour12: false,
          }
        )}`,
        '',
        filterCount > 0 ? `${i18n.t('FILTERS_APPLIED_KEY')}:` : '',
        appliedFilters.join('\n'),
        '',
      ],
      10,
      10,
      { lineHeightFactor: 1.5, maxWidth: 200 }
    );
    autoTable(doc, {
      head: getHeadersPDF,
      margin: { top: topMargin },
      styles: { lineWidth: 0.3, lineColor: palette.borderGray, fontSize: 9 },
      headStyles: {
        fillColor: palette.deepBlue,
        textColor: palette.offWhite,
        fontSize: 9,
      },
      body: getBodyListPDF,
      didDrawPage: data => {
        if (pageNumber > 0) {
          // eslint-disable-next-line no-param-reassign
          data.settings.margin.top = 15;
        }
        pageNumber += 1;
        const imageWidth = 20;
        const imageHeight = 6.67;
        const xPosition = doc.internal.pageSize.getWidth() - imageWidth - 10;
        const yPosition = 5;

        doc.addImage(
          image,
          'PNG',
          xPosition,
          yPosition,
          imageWidth,
          imageHeight
        );
      },
    });
    doc.save(
      `${context}-${new Date().toLocaleString('pt-BR', {
        hour12: false,
      })}-filtered.pdf`
    );
  };

  const onClickExportPDF = () => {
    generatePDFButton();
    messagesRequest({
      type: 'success',
      message: i18n.t('PDF_GENERATED_SUCCESSFULLY_KEY'),
    });
  };

  return (
    <>
      <ButtonGroup ref={anchorRef}>
        <IconButton className={classes.pdfButton} onClick={handleToggle}>
          <GetAppIcon />
        </IconButton>
      </ButtonGroup>

      <Popper
        placement="bottom-start"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        style={{ zIndex: 2 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  <>
                    <MenuItem
                      id={'export-as-pdf'}
                      onClick={event => {
                        handleOpen(
                          i18n.t('EXPORT_PDF_KEY'),
                          event,
                          onClickExportPDF
                        );
                      }}
                      className={classes.menuItem}
                    >
                      {i18n.t('EXPORT_PDF_KEY')}
                    </MenuItem>
                    {!!csvAction && (
                      <MenuItem
                        id={'export-as-csv'}
                        onClick={event => {
                          handleOpen(
                            i18n.t('EXPORT_CSV_KEY'),
                            event,
                            csvAction
                          );
                        }}
                        className={classes.menuItem}
                      >
                        {i18n.t('EXPORT_CSV_KEY')}
                      </MenuItem>
                    )}
                  </>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
};

const mapStateToProps = null;

export default connect(mapStateToProps, {
  messagesRequest,
})(ButtonExport);
